import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { tasksAPI } from '../api/endpoints';
import type { BackgroundTask } from '../types';

const POLL_INTERVAL_MS = 2000;

interface UseBackgroundTaskOptions {
  /** Called when the task completes successfully */
  onComplete?: (task: BackgroundTask) => void;
  /** Called when the task fails */
  onError?: (task: BackgroundTask) => void;
  /** Label for toast notifications (e.g. "Email sync", "CSV import") */
  label?: string;
  /** Override success message based on task result */
  getSuccessMessage?: (task: BackgroundTask) => string;
}

function DismissableToast({ message, toastId }: { message: string; toastId: string }) {
  return (
    <span className="flex items-center gap-3">
      <span>{message}</span>
      <button
        onClick={() => toast.dismiss(toastId)}
        className="text-slate-400 hover:text-white text-xl leading-none font-light"
        aria-label="Dismiss"
      >
        ×
      </button>
    </span>
  );
}

export function useBackgroundTask(options: UseBackgroundTaskOptions = {}) {
  const [task, setTask] = useState<BackgroundTask | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const toastIdRef = useRef<string | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const poll = useCallback(async (id: string) => {
    try {
      const response = await tasksAPI.get(id);
      const t = response.data;
      setTask(t);

      const label = optionsRef.current.label || 'Task';

      if (t.status === 'running' || t.status === 'pending') {
        const message = t.message || `${label} in progress...`;
        const progressPct = Math.round(t.progress);
        toastIdRef.current = toast.loading(
          `${message} (${progressPct}%)`,
          { id: toastIdRef.current || undefined },
        );
      }

      if (t.status === 'completed') {
        stopPolling();
        if (toastIdRef.current) {
          const tid = toastIdRef.current;
          const successMsg = optionsRef.current.getSuccessMessage
            ? optionsRef.current.getSuccessMessage(t)
            : `${label} completed`;
          toast.success(
            (toastObj) => <DismissableToast message={successMsg} toastId={toastObj.id} />,
            { id: tid, duration: Infinity },
          );
          toastIdRef.current = null;
        }
        optionsRef.current.onComplete?.(t);
      } else if (t.status === 'failed') {
        stopPolling();
        if (toastIdRef.current) {
          const tid = toastIdRef.current;
          const errMsg = t.error || `${label} failed`;
          toast.error(
            (toastObj) => <DismissableToast message={errMsg} toastId={toastObj.id} />,
            { id: tid, duration: Infinity },
          );
          toastIdRef.current = null;
        }
        optionsRef.current.onError?.(t);
      }
    } catch {
      // Keep polling on transient errors
    }
  }, [stopPolling]);

  const startPolling = useCallback((id: string) => {
    stopPolling();
    setTaskId(id);
    setTask({ id, type: '', status: 'pending', progress: 0, message: '', result: null, error: null, created_at: '', updated_at: '' });

    const label = optionsRef.current.label || 'Task';
    toastIdRef.current = toast.loading(`${label} starting...`);

    poll(id);
    intervalRef.current = setInterval(() => poll(id), POLL_INTERVAL_MS);
  }, [poll, stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
    }
    setTask(null);
    setTaskId(null);
  }, [stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
      }
    };
  }, [stopPolling]);

  return {
    task,
    taskId,
    isActive: task?.status === 'pending' || task?.status === 'running',
    isComplete: task?.status === 'completed',
    isFailed: task?.status === 'failed',
    startPolling,
    stopPolling,
    reset,
  };
}
